import React, { useState, useEffect, useRef } from "react";
import { Button, Modal, Form, Table, Row, Col, Image } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from 'react-hot-toast';
import DeleteDialog from "../../components/DeleteDialog";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ✅ Utility to format file size
const humanFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  return (bytes / Math.pow(1024, i)).toFixed(2) + " " + sizes[i];
};

const validateImageRatio = (file) => {
  return new Promise((resolve) => {
    const img = new window.Image();

    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      console.log("Image Size:", width, height);

      const ratio = width / height;

      // ✅ 3:2 ratio = 1.5
      if (Math.abs(ratio - 1.5) <= 0.05) {
        resolve(true);
      } else {
        resolve(false);
      }
    };

    img.onerror = () => resolve(false);

    img.src = URL.createObjectURL(file);
  });
};




// Validation Schema
const validationSchema = Yup.object().shape({
  title1: Yup.string()
    .required("Title 1 is required")
    .min(3, "Title 1 must be at least 3 characters")
    .max(200, "Title 1 must not exceed 200 characters"),
  title1_image: Yup.mixed()
    .nullable()
    .test("fileSize", "Image must be less than 5MB", (value) => {
      if (!value) return true;
      return value.size <= 5 * 1024 * 1024;
    })
    .test("fileType", "Only image files are allowed (JPEG, PNG, GIF, WebP)", (value) => {
      if (!value) return true;
      return ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(value.type);
    }),
  short_description: Yup.string()
    .required("Short description is required")
    .min(10, "Short description must be at least 10 characters")
    .max(50, "Short description must not exceed 50 characters"),
  category: Yup.string()
    .required("Category is required"),
  published_date: Yup.date()
    .required("Published date is required")
    .typeError("Published date must be a valid date"),
  read_time: Yup.string()
    .required("Read time is required"),
  author_name: Yup.string()
    .required("Author name is required")
    .min(2, "Author name must be at least 2 characters"),
  title1_long_description: Yup.string()
    .required("Title 1 long description is required")
    .min(20, "Title 1 long description must be at least 20 characters"),
  title2: Yup.string()
    .min(3, "Title 2 must be at least 3 characters")
    .max(200, "Title 2 must not exceed 200 characters"),
  description2: Yup.string()
    .max(500, "Description 2 must not exceed 500 characters"),
  title2_image: Yup.mixed()
    .nullable()
    .test("fileSize", "Image must be less than 5MB", (value) => {
      if (!value) return true;
      return value.size <= 5 * 1024 * 1024;
    })
    .test("fileType", "Only image files are allowed (JPEG, PNG, GIF, WebP)", (value) => {
      if (!value) return true;
      return ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(value.type);
    }),
});

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const createdObjectUrls = useRef([]);

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      title1: "",
      title1_image: null,
      title1_preview: null,
      short_description: "",
      category: "",
      published_date: "",
      read_time: "",
      author_name: "",
      title1_long_description: "",
      title2: "",
      description2: "",
      title2_image: null,
      title2_preview: null,
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/superadmin/blogs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      setBlogs(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load blogs");
    }
  };

  const handleShow = () => setShow(true);

  const handleClose = () => {
    try {
      createdObjectUrls.current.forEach((u) => {
        if (u && u.startsWith("blob:")) URL.revokeObjectURL(u);
      });
    } catch (e) {
      // ignore
    }
    createdObjectUrls.current = [];
    setShow(false);
    setEditingId(null);
    formik.resetForm();
  };

  const openEdit = async (id) => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/superadmin/blogs/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to fetch blog");
      const data = await res.json();
      formik.setValues({
        title1: data.title1 || "",
        title1_image: null,
        title1_preview: data.title1_image || null,
        short_description: data.short_description || "",
        category: data.category || "",
        published_date: data.published_date ? data.published_date.split("T")[0] : "",
        read_time: data.read_time || "",
        author_name: data.author_name || "",
        title1_long_description: data.title1_long_description || "",
        title2: data.title2 || "",
        description2: data.description2 || "",
        title2_image: null,
        title2_preview: data.title2_image || null,
      });
      setEditingId(id);
      setShow(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load blog for editing");
    }
  };

  const handleFileChange = async (e) => {
    const { name } = e.target;
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    // Validate file type
    if (!["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)) {
      toast.error("Only image files allowed (JPEG, PNG, GIF, WebP)");
      e.target.value = "";
      return;
    }

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Image must be less than 5MB. Current: ${humanFileSize(file.size)}`);
      e.target.value = "";
      return;
    }

    // ⭐ NEW — Validate Image Ratio
    const isValidRatio = await validateImageRatio(file);

    if (!isValidRatio) {
      toast.error("Image must be in 3:2 ratio");
      e.target.value = "";
      return;
    }

    // Update Formik values
    formik.setFieldValue(name, file);

    try {
      const url = URL.createObjectURL(file);
      createdObjectUrls.current.push(url);

      if (name === "title1_image") {
        formik.setFieldValue("title1_preview", url);
      } else if (name === "title2_image") {
        formik.setFieldValue("title2_preview", url);
      }
    } catch (error) {
      console.error("Error creating object URL:", error);
    }
  };

  async function handleSubmit(values) {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const formData = new FormData();
      formData.append("title1", values.title1);
      formData.append("short_description", values.short_description);
      formData.append("category", values.category);
      formData.append("published_date", values.published_date);
      formData.append("read_time", values.read_time);
      formData.append("author_name", values.author_name);
      formData.append("title1_long_description", values.title1_long_description);
      formData.append("title2", values.title2);
      formData.append("description2", values.description2);
      if (values.title1_image) formData.append("title1_image", values.title1_image);
      if (values.title2_image) formData.append("title2_image", values.title2_image);

      let res;
      if (editingId) {
        res = await fetch(`${BASE_URL}/api/superadmin/blogs/${editingId}`, {
          method: "PUT",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      } else {
        res = await fetch(`${BASE_URL}/api/superadmin/blogs`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      }

      if (!res.ok) {
        if (res.status === 401) throw new Error("Unauthorized (401)");
        throw new Error("Failed to save");
      }

      await fetchBlogs();
      handleClose();
      toast.success("Blog saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save blog: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  }



  const removePreview = (which) => {
    if (which === "title1") {
      if (formik.values.title1_preview && formik.values.title1_preview.startsWith("blob:")) {
        URL.revokeObjectURL(formik.values.title1_preview);
      }
      formik.setFieldValue("title1_preview", null);
      formik.setFieldValue("title1_image", null);
    }
    if (which === "title2") {
      if (formik.values.title2_preview && formik.values.title2_preview.startsWith("blob:")) {
        URL.revokeObjectURL(formik.values.title2_preview);
      }
      formik.setFieldValue("title2_preview", null);
      formik.setFieldValue("title2_image", null);
    }
  };

  return (
    <div className="py-4">
      <div className="container-fluid">
        {/* Page Header */}
        <Row className="mb-5 align-items-center">
          <Col>
            <h2 className="global-heading">Blog Management</h2>
          </Col>
          <Col className="text-end">
            <Button
              onClick={handleShow}
              className="btn-custom"
            >
              Add Blog
            </Button>
          </Col>
        </Row>

        {/* Blogs Table */}
        {blogs && blogs.length > 0 ? (
          <div className="AdminTable-root">
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>S.no</th>
                  <th style={{ width: "15%" }}>Image</th>
                  <th style={{ width: "18%" }}>Category</th>
                  <th style={{ width: "30%" }}>Title</th>
                  <th style={{ width: "14%" }}>Date</th>
                  <th style={{ width: "15%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((b, i) => (
                  <tr key={b.id}>
                    <td>{String(i + 1).padStart(2, "0")}</td>
                    <td>
                      {b.title1_image ? (
                        <Image
                          src={b.title1_image}
                          thumbnail
                          alt={b.title1}
                          style={{ maxWidth: 70, height: "auto" }}
                        />
                      ) : (
                        <div className="bg-light p-2 text-center rounded" style={{ minWidth: 70, minHeight: 50 }}>
                          <small className="text-muted">No Image</small>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-info">{b.category}</span>
                    </td>
                    <td className="text-truncate">{b.title1}</td>
                    <td>{b.published_date ? new Date(b.published_date).toLocaleDateString("en-IN") : "N/A"}</td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          className="border-0 fw-semibold primary-class bg-transparent primary-class"
                          style={{ cursor: "pointer" }}
                          onClick={() => openEdit(b.id)}
                        >
                          <span className="material-icons me-1" style={{ fontSize: 16 }}>
                            edit
                          </span>
                        </Button>

                        <DeleteDialog
                          path={`${BASE_URL}/api/superadmin/blogs/${b.id}`}
                          message="Blog deleted successfully"
                          onSuccess={fetchBlogs}
                          icon="button"
                          btnProps={{
                            size: "sm",
                            variant: "light",
                            className: "fw-normal d-flex align-items-center",
                          }}
                        />



                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted fw-5">No blogs found</p>
            <Button onClick={handleShow} className="btn-custom mt-2">
              Create First Blog
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Blog Modal */}
      <Modal show={show} onHide={handleClose} size="lg" centered className="form-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? "Edit Blog" : "Add Blog"}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={formik.handleSubmit} className="form-modal">
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {/* Upload Title Image */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold mb-3">Upload Title Image</Form.Label>
              <div className="border border-2 border-primary-light rounded-3 p-4 text-center bg-light">
                <input
                  type="file"
                  name="title1_image"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="title1_image_input"
                />
                <small className="text-muted">
                  Recommended size: 800 × 500 px (3:2 ratio)
                </small>
                <label htmlFor="title1_image_input" className="mb-0 cursor-pointer d-block">
                  <div className="fs-1">📸</div>
                  <div className="fw-bold text-primary mb-1">Click to upload</div>
                  <small className="text-muted">Max 5MB • JPEG, PNG, GIF, WebP</small>
                </label>
              </div>
              {formik.values.title1_preview && (
                <div className="img-preview-sm mt-3">
                  <Image src={formik.values.title1_preview} thumbnail />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => removePreview("title1")}
                  >
                    ×
                  </button>
                </div>
              )}
              {formik.touched.title1_image && formik.errors.title1_image && (
                <Form.Control.Feedback type="invalid" className="d-block mt-2">
                  {formik.errors.title1_image}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Title */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Title</Form.Label>
              <Form.Control
                name="title1"
                value={formik.values.title1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.title1 && !!formik.errors.title1}
                placeholder="Enter blog title"
              />
              {formik.touched.title1 && formik.errors.title1 && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {formik.errors.title1}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Short Description */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Short Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="short_description"
                value={formik.values.short_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.short_description && !!formik.errors.short_description}
                placeholder="Brief summary of your blog"
              />
              <small className="text-muted d-block mt-1">
                {formik.values.short_description.length}/50 characters
              </small>
              {formik.touched.short_description && formik.errors.short_description && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {formik.errors.short_description}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Category & Published Date */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Category</Form.Label>
                  <Form.Control
                    name="category"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.category && !!formik.errors.category}
                    placeholder="e.g., Health, News, Tips"
                  />
                  {formik.touched.category && formik.errors.category && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {formik.errors.category}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Published Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="published_date"
                    value={formik.values.published_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.published_date && !!formik.errors.published_date}
                  />
                  {formik.touched.published_date && formik.errors.published_date && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {formik.errors.published_date}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Read Time & Author */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Read Time</Form.Label>
                  <Form.Control
                    name="read_time"
                    value={formik.values.read_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.read_time && !!formik.errors.read_time}
                    placeholder="e.g., 5 min read"
                  />
                  {formik.touched.read_time && formik.errors.read_time && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {formik.errors.read_time}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Author Name</Form.Label>
                  <Form.Control
                    name="author_name"
                    value={formik.values.author_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.author_name && !!formik.errors.author_name}
                    placeholder="Your name"
                  />
                  {formik.touched.author_name && formik.errors.author_name && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {formik.errors.author_name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Title1 Long Description */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Title1 Long Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="title1_long_description"
                value={formik.values.title1_long_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.title1_long_description && !!formik.errors.title1_long_description}
                placeholder="Detailed content for main title"
              />
              <small className="text-muted d-block mt-1">
                {formik.values.title1_long_description.length} characters
              </small>
              {formik.touched.title1_long_description && formik.errors.title1_long_description && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {formik.errors.title1_long_description}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <hr className="my-4" />

            {/* Title2 */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Title2</Form.Label>
              <Form.Control
                name="title2"
                value={formik.values.title2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.title2 && !!formik.errors.title2}
                placeholder="Enter secondary title"
              />
              {formik.touched.title2 && formik.errors.title2 && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {formik.errors.title2}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Description2 */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Description2</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description2"
                value={formik.values.description2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.description2 && !!formik.errors.description2}
                placeholder="Additional content (optional)"
              />
              <small className="text-muted d-block mt-1">
                {formik.values.description2.length}/500 characters
              </small>
              {formik.touched.description2 && formik.errors.description2 && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {formik.errors.description2}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Upload Title2 Image */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold mb-3">Upload Title2 Image</Form.Label>
              <div className="border border-2 border-primary-light rounded-3 p-4 text-center bg-light">
                <input
                  type="file"
                  name="title2_image"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="title2_image_input"
                />
                <label htmlFor="title2_image_input" className="mb-0 cursor-pointer d-block">
                  <div className="fs-1">🖼️</div>
                  <div className="fw-bold text-primary mb-1">Click to upload</div>
                  <small className="text-muted">Max 5MB • JPEG, PNG, GIF, WebP</small>
                </label>
              </div>
              {formik.values.title2_preview && (
                <div className="img-preview-sm mt-3">
                  <Image src={formik.values.title2_preview} thumbnail />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => removePreview("title2")}
                  >
                    ×
                  </button>
                </div>
              )}
              {formik.touched.title2_image && formik.errors.title2_image && (
                <Form.Control.Feedback type="invalid" className="d-block mt-2">
                  {formik.errors.title2_image}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Modal.Body>

          <Modal.Footer className="border-top">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-custom"
              disabled={loading || !formik.isValid}
            >
              {loading ? "Saving..." : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogPage;
